export const COVER_SIZE = 3000;

// Vue can update text before a long canvas draw without the browser painting it.
// Yield across a painted frame so the busy state is visible before encoding.
export function waitForCoverPaint() {
  return new Promise(resolve => {
    if (typeof requestAnimationFrame === 'function') {
      requestAnimationFrame(() => requestAnimationFrame(resolve));
    } else {
      setTimeout(resolve, 0);
    }
  });
}
export const coverDesigns = [
  { id: 'orbit', premium: true },
  { id: 'wave', premium: true },
  { id: 'frame', premium: true },
  { id: 'type', premium: false },
];
export const coverPalettes = [
  { id: 'after-hours', background: '#141b24', primary: '#d6edaf', accent: '#7999ba', text: '#f5f4eb' },
  { id: 'ultraviolet', background: '#201335', primary: '#e4baff', accent: '#7566df', text: '#fff2f9' },
  { id: 'heat', background: '#381916', primary: '#ffb092', accent: '#e64d35', text: '#fff1db' },
  { id: 'paper', background: '#eae5d6', primary: '#2d493e', accent: '#a5b690', text: '#202f29' },
  { id: 'cobalt', background: '#111d5b', primary: '#b1d3ff', accent: '#557aff', text: '#f3f5ff' },
  { id: 'mono', background: '#181818', primary: '#e6e6df', accent: '#7b7b77', text: '#f7f7ed' },
];

export function normalizeDesign(id) {
  if (coverDesigns.some(design => design.id === id)) return id;
  return { text: 'type', minimal: 'frame', vintage: 'frame', gradient: 'wave', artistic: 'wave', abstract: 'orbit' }[id] || 'orbit';
}

// Character wrapping also supports long unbroken titles and non-Latin scripts.
export function fitCoverText(ctx, text, width, maxHeight, startSize) {
  const words = String(text).trim().split(/\s+/u);
  for (let size = startSize; size >= 8; size -= 2) {
    ctx.font = `600 ${size}px Inter, Arial, sans-serif`;
    const lines = [];
    let line = '';
    for (const word of words) {
      const candidate = line ? `${line} ${word}` : word;
      if (ctx.measureText(candidate).width <= width) { line = candidate; continue; }
      if (line) lines.push(line);
      line = '';
      for (const char of word) {
        if (line && ctx.measureText(line + char).width > width) { lines.push(line); line = ''; }
        line += char;
      }
    }
    if (line) lines.push(line);
    if (lines.length * size * 1.1 <= maxHeight || size === 8) return { lines, size };
  }
}

// A single normalized drawing makes thumbnails, preview and export identical.
export function drawCover(canvas, options) {
  const ctx = canvas.getContext('2d');
  if (!ctx) throw new Error('Canvas unavailable');
  const { design = 'orbit', colors = coverPalettes[0], title = '', artist = '', showText = true, position = 'bottom', variant = 0 } = options;
  const { background, primary, accent, text } = colors;
  ctx.save();
  ctx.scale(canvas.width / 1000, canvas.height / 1000);
  ctx.fillStyle = background;
  ctx.fillRect(0, 0, 1000, 1000);

  if (design === 'orbit') {
    ctx.save();
    ctx.translate(510, 460);
    ctx.rotate(-0.42 + (variant % 5) * 0.14);
    const sphere = ctx.createRadialGradient(-130, -140, 12, 0, 0, 325);
    sphere.addColorStop(0, primary); sphere.addColorStop(0.5, accent); sphere.addColorStop(1, background);
    ctx.fillStyle = sphere; ctx.beginPath(); ctx.arc(0, 0, 310, 0, Math.PI * 2); ctx.fill();
    for (let i = 0; i < 38; i++) {
      ctx.strokeStyle = i % 3 === 0 ? primary : accent;
      ctx.globalAlpha = 0.25 + i / 100;
      ctx.lineWidth = 1.4;
      ctx.beginPath(); ctx.ellipse(0, 0, 360 + i * 2.8, 70 + i * 4.8, 0, 0, Math.PI * 2); ctx.stroke();
    }
    ctx.restore();
  } else if (design === 'wave') {
    ctx.save(); ctx.translate(500, 440); ctx.rotate(-0.35 + (variant % 5) * 0.16);
    for (let i = 0; i < 42; i++) {
      const gradient = ctx.createLinearGradient(-400, 0, 400, 0);
      gradient.addColorStop(0, accent); gradient.addColorStop(0.5, primary); gradient.addColorStop(1, accent);
      ctx.strokeStyle = gradient; ctx.lineWidth = 4;
      ctx.beginPath();
      for (let x = -500; x <= 500; x += 5) {
        const y = Math.sin(x / 180 + i / 12) * 150 + (i - 21) * 13;
        if (x === -500) ctx.moveTo(x, y); else ctx.lineTo(x, y);
      }
      ctx.stroke();
    }
    ctx.restore();
  } else if (design === 'frame') {
    const frameY = position === 'top' ? 390 : 70;
    ctx.fillStyle = primary; ctx.fillRect(70, frameY, 860, 540);
    ctx.save(); ctx.beginPath(); ctx.rect(70, frameY, 860, 540); ctx.clip();
    ctx.fillStyle = accent; ctx.beginPath(); ctx.arc(580 + variant % 4 * 30, frameY + 270, 295, 0, Math.PI * 2); ctx.fill();
    ctx.strokeStyle = background; ctx.lineWidth = 2;
    for (let i = 0; i < 28; i++) { ctx.beginPath(); ctx.moveTo(70, frameY + 25 + i * 22); ctx.lineTo(930, frameY + 280 + i * 22); ctx.stroke(); }
    ctx.restore();
  } else {
    ctx.fillStyle = primary; ctx.fillRect(70, 70, 60 + variant % 4 * 20, 60);
    ctx.strokeStyle = accent; ctx.lineWidth = 2;
    for (let i = 0; i < 9; i++) { ctx.beginPath(); ctx.arc(825, 825, 60 + i * 17 + variant % 5 * 5, 0, Math.PI * 2); ctx.stroke(); }
  }

  if (showText) {
    const top = position === 'top' && design !== 'type';
    if (design === 'orbit' || design === 'wave') {
      const fade = ctx.createLinearGradient(0, top ? 0 : 600, 0, top ? 500 : 1000);
      fade.addColorStop(0, top ? background : `${background}00`);
      fade.addColorStop(1, top ? `${background}00` : background);
      ctx.fillStyle = fade; ctx.fillRect(0, top ? 0 : 600, 1000, top ? 500 : 400);
    }
    ctx.fillStyle = text;
    const type = design === 'type';
    const maxHeight = type ? 550 : 230;
    const fitted = fitCoverText(ctx, title, 860, maxHeight, type ? 150 : 96);
    ctx.font = `600 ${fitted.size}px Inter, Arial, sans-serif`;
    ctx.textBaseline = 'top'; ctx.textAlign = 'left';
    const y = type ? 210 : top ? 75 : 885 - fitted.lines.length * fitted.size * 1.1;
    fitted.lines.forEach((line, index) => ctx.fillText(line, 70, y + index * fitted.size * 1.1));
    const credit = fitCoverText(ctx, artist, 860, 60, 28);
    ctx.font = `500 ${credit.size}px Inter, Arial, sans-serif`;
    credit.lines.forEach((line, index) => ctx.fillText(line, 73, (top ? y + fitted.lines.length * fitted.size * 1.1 + 22 : 915) + index * credit.size * 1.1));
  }
  ctx.restore();
}

export async function exportCover(options) {
  if (document.fonts?.ready) await document.fonts.ready;
  const canvas = document.createElement('canvas');
  canvas.width = COVER_SIZE; canvas.height = COVER_SIZE;
  drawCover(canvas, options);
  const blob = await new Promise(resolve => canvas.toBlob(resolve, 'image/jpeg', 0.95));
  if (!blob) throw new Error('Cover export failed');
  return {
    dataUrl: canvas.toDataURL('image/jpeg', 0.95),
    file: new File([blob], 'release-cover.jpg', { type: 'image/jpeg' }),
    width: COVER_SIZE, height: COVER_SIZE,
    template: options.design,
    colors: { ...options.colors },
  };
}
